<template>
  <div 
    :class="`AMCharts--${graph}`"
    class="AMCharts" ref="chartdiv"></div>
</template>

<script>

import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

am4core.useTheme(am4themes_animated);

export default {
  name: "AMCharts",
  props: {
    graph: {
      type: String,
      default: 'focus',
      validator: function(value) {
        return ['focus', 'market', 'chargers'].includes(value)
      }
    },
    data: {
      type: String,
      default: ''
    },
    legendId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      chart: null
    }
  },
  computed: {
    processedData() {
      return JSON.parse(this.data)
    }
  },
  watch: {

    // Handling updated data passed through the prop
    processedData(newValue) {
      if (this.chart) {
        newValue.forEach((val, index) => {
          switch(this.graph) {
            case 'chargers': 
              this.chart.data[index].chargers = val.chargers
              break;

            case 'focus': 
              this.chart.data[index].focus = val.focus
              break;

            case 'market':
              this.chart.data[index].partner = val.partner
              break;
          }
          
          this.chart.data[index].projects = val.projects
        })
        
        this.chart.invalidateRawData()
      } 
    }
  },
  mounted() {
    if (this.graph === 'focus') {
      this.createFocusGraph()
    }
    if (this.graph === 'market') {
      this.createMarketGraph()
    }
    if (this.graph === 'chargers') {
      this.createChargerGraph()
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {

    createFocusGraph() {
      let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)

      chart.data = this.processedData

      let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
      categoryAxis.dataFields.category = "focus"
      // categoryAxis.title.text = 'Focus'
      categoryAxis.renderer.grid.template.strokeOpacity = 0;
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.minGridDistance = 30;
      categoryAxis.renderer.inside = true;
      categoryAxis.renderer.labels.template.fill = am4core.color("#fff");
      categoryAxis.renderer.labels.template.fontSize = 16;

      let valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
      // valueAxis.title.text = 'Number of projects'
      valueAxis.renderer.grid.template.strokeOpacity = 0;

      /* Create series */
      var series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueX = "projects";
      series.dataFields.categoryY = "focus";
      series.columns.template.tooltipText = "Focus: {categoryY}\nProjects: {valueX}";
      series.columns.template.fill = am4core.color("#1ECDA4");
      series.columns.template.stroke = am4core.color("#fff");
      series.columns.template.strokeWidth = 1;
      series.columns.template.strokeOpacity = 0.5;
      series.columns.template.height = am4core.percent(70);
      series.reverseOrder = true;

      series.tooltip.label.fill = am4core.color("#fff");

      this.chart = chart;
    },

    createMarketGraph() {

      this.chart = this.createDonutGraph({
        valueName: 'projects',
        categoryName: 'partner',
        colors: ['#7F8FA4', '#354052', '#16BAC0', '#FF715B', '#FBB13C', '#382C7A']
      })
    },

    createChargerGraph() {

      this.chart = this.createDonutGraph({
        valueName: 'projects',
        categoryName: 'chargers',
        colors: ['#7F8FA4', '#354052', '#CED0DA']
      })
    },

    createDonutGraph({ valueName, categoryName, colors }) {
      // Create chart instance
      let chart = am4core.create(this.$refs.chartdiv, am4charts.PieChart);

      // Set the data
      chart.data = this.processedData;

      // Add and configure Series
      let pieSeries = chart.series.push(new am4charts.PieSeries());
      pieSeries.dataFields.value = valueName
      pieSeries.dataFields.category = categoryName;

      // Colors
      const sliceColors = colors
      pieSeries.slices.template.adapter.add("fill", (fill, target) => {
        return am4core.color(sliceColors[target.dataItem.index]);
      })

      // Turn the pie chart into a donut chart
      chart.innerRadius = am4core.percent(40);

      // Disable pullOut (select) effect
      chart.pullOutRadius = 0

      // Hover effect
      // Default value. Set to allow changing opacity on hover
      pieSeries.slices.template.fillOpacity = 1;
      let hs = pieSeries.slices.template.states.getKey("hover");
      hs.properties.scale = 1;
      hs.properties.fillOpacity = 0.5;

      // Labels
      chart.startAngle = 0
      chart.endAngle = 360;
      chart.hideLabelsPercent = 4;
      pieSeries.ticks.template.disabled = true
      pieSeries.alignLabels = false
      pieSeries.labels.template.text = "{value.percent.formatNumber('#.0')}%"

      // Legend
      chart.legend = new am4charts.Legend()

      // Legend in external Container
      // https://www.amcharts.com/docs/v4/tutorials/chart-legend-in-an-external-container/#Dynamically_resizing_legend_div
      if (this.legendId) {
        let legendContainer = am4core.create(this.legendId, am4core.Container)
        legendContainer.width = am4core.percent(100)
        legendContainer.height = am4core.percent(100)
        chart.legend.parent = legendContainer

        let resizeLegend = (ev) => {
          document.getElementById(this.legendId).style.height = (chart.legend.contentHeight + 20) + "px";
        }
        chart.events.on("datavalidated", resizeLegend);
        chart.events.on("maxsizechanged", resizeLegend);
      }

      // Event - click on slice
      pieSeries.slices.template.events.on("hit", (ev) => {
        let series = ev.target.dataItem.component
        let result = []
        series.slices.each((slice) => {
          if (slice.isActive) {
            result.push(slice.dataItem.properties.category)
          }
        })
        // Let the world know
        this.$emit('sliceClick', { 
          graph: this.graph,
          values: result
        })
      })

      // Event - click on legend
      chart.legend.itemContainers.template.events.on("hit", (ev) => {
        
        let slice = ev.target.dataItem.dataContext.slice
        let name = ev.target.dataItem.dataContext.category
        setTimeout(() => {

          // Let the world know
          this.$emit('legendClick', { 
            graph: this.graph,
            category: name,
            hidden: slice.isHiding
          })

        }, 1)
      })

      return chart
    }
  }
}
</script>